import { Injectable } from '@angular/core';
import { BOOT_LOCALE } from 'i18n/i18n.service';
import { getEnvironmentDefaultLocale } from 'i18n/ui-language';
import { SettingsService } from 'services/settings.service';
import { LoggingService } from 'services/logging.service';
import { UiLanguageReconciler } from 'services/ui-language-reconcile';
import type { Locales } from '../../gensrc/i18n/i18n-types';

/**
 * Sign-in reconciliation for the UI language (#4147): after the account's
 * settings have synced down, decide whether the language on screen still matches
 * what the user has chosen, and reload once if it does not.
 *
 * This class is only the wiring. It supplies the browser's two storage areas,
 * the account's language preference and telemetry — everything the rule needs
 * from the outside world except the reload, which the caller passes in. The rule
 * itself is in `UiLanguageReconciler` (services/ui-language-reconcile.ts), and
 * the decision at its centre is the pure, separately-tested
 * `decideUiLanguageReconcile` in i18n/ui-language.ts. The whole design is in
 * design-docs/i18n-translation.md.
 *
 * Lifted out of `AppComponent` with no behavior change. That component still
 * owns WHEN the reconcile runs — on both the cold-start and warm-resume paths,
 * right after settings sync — and still owns the reload itself.
 */
@Injectable({ providedIn: 'root' })
export class UiLanguageReconcileService {
  private readonly reconciler: UiLanguageReconciler;

  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
  ) {
    this.reconciler = new UiLanguageReconciler({
      local: localStorage,
      session: sessionStorage,
      readSavedPreference: () => this.settings.uiLanguage,
      writeSavedPreference: (locale: Locales) => {
        this.settings.uiLanguage = locale;
      },
      saveSavedPreferenceAsync: () => this.settings.saveAsync(),
      reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      reportException: (err) => this.logging.trackAbnormalException(err),
    });
  }

  /**
   * Run the reconcile for the signed-in user.
   *
   * `requestReload` reloads the whole app so the module-level translations
   * rebind. It is a parameter rather than something this service holds because
   * reloading belongs to `AppComponent`, and passing it per call means it can
   * never be left half-wired.
   */
  reconcileAfterSettingsSync(currentUid: string, requestReload: () => void): void {
    // BOOT_LOCALE is the locale the translations are ACTUALLY bound to, captured
    // once when the translation module loaded. See the parameter's own comment in
    // UiLanguageReconciler for why a fresh device-key read would be wrong here.
    this.reconciler.reconcileAfterSettingsSync(
      currentUid,
      BOOT_LOCALE,
      getEnvironmentDefaultLocale(),
      requestReload,
    );
  }
}
